import { map, path, isEmpty, pluck } from 'ramda';
import { FileSystemDirectoryReader, FileSystemFileEntry, FileSystemEntry, FileWithPath } from './fileEntry.types';

export const createFolderStructure =
  (folderNames: string[], currentFolder, createNewFolderFn) =>
  async (fileList: FileWithPath[]): Promise<File[]> => {
    if (isEmpty(folderNames)) {
      return extractFile(fileList);
    }
    const folderIds = {};
    const initialRoot = currentFolder?.id;

    return folderNames
      .reduce((promiseAcc, folder) => {
        return promiseAcc.then((chainResults) =>
          createFoldersByPath(folder, createNewFolderFn, folderIds, { initialRoot }).then((currentResult) => [
            ...chainResults,
            currentResult,
          ]),
        );
      }, Promise.resolve([]))
      .then(() => {
        fileList.forEach(({ file, path }) => {
          const relativeFolder = path.substring(1);
          if (path && folderIds[relativeFolder]) {
            // @ts-expect-error
            file.mmgrFolderId = folderIds[relativeFolder];
          }
        });
        return extractFile(fileList);
      });
  };

const createFoldersByPath = async (name: string, createNewFolderFn, folderIds, options = { initialRoot: '/' }) => {
  const folderPath = name.substring(1).split('/');
  const currentLevel = [];
  for (const folder of folderPath) {
    const folderPath = [...currentLevel, folder].join('/');
    const parentId = folderIds[currentLevel.join('/')] || options.initialRoot;

    if (!folderIds[folderPath]) {
      await createNewFolderFn({ name: folder, parentId })
        .then(path(['data', 'id']))
        .then((folderId: string) => {
          currentLevel.push(folder);
          folderIds[folderPath] = folderId;
        });
    } else {
      currentLevel.push(folder);
    }
  }

  return folderIds;
};

export const extractFile = pluck('file');

export const getAllDroppedFiles = async (
  dataTransferItemList: DataTransferItemList,
): Promise<{ files: FileWithPath[]; folders: any[] }> => {
  const fileEntries = [];
  const dirEntries = [];
  const queue = map((item) => item.webkitGetAsEntry(), dataTransferItemList);

  while (queue.length > 0) {
    const entry = queue.shift();
    if (entry && entry.isFile) {
      fileEntries.push(entry);
    } else if (entry && entry.isDirectory) {
      dirEntries.push(entry.fullPath);
      const reader = (entry as FileSystemDirectoryEntry).createReader();
      queue.push(...(await readAllDirectoryEntries(reader)));
    }
  }
  return { files: await toFilesWithPath(fileEntries), folders: dirEntries };
};

const toFilesWithPath = (fileEntries: FileSystemFileEntry[]) =>
  Promise.all<FileWithPath>(
    map(
      (entry) =>
        new Promise((resolve, reject) =>
          // fullPath is lost after getting file out of entry
          // so take .fullPath and remove filename to save it
          entry.file(
            (f) =>
              resolve({
                file: f,
                path: entry.fullPath.substring(0, entry.fullPath.lastIndexOf(`/${f.name}`)),
              }),
            reject,
          ),
        ),
      fileEntries,
    ),
  );

const readAllDirectoryEntries = async (directoryReader) => {
  const entries = [];
  let readEntries = await readEntriesPromise(directoryReader);
  while (readEntries.length > 0) {
    entries.push(...readEntries);
    readEntries = await readEntriesPromise(directoryReader);
  }
  return entries;
};

const readEntriesPromise = async (directoryReader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> => {
  try {
    return await new Promise((resolve, reject) => {
      directoryReader.readEntries(resolve, reject);
    });
  } catch (err) {
    console.log(err);
  }
};
